/**
 * プロシージャル GIF エンコーダ (GifEncoder)
 * aidlc-docs/construction/media_export/docs/encoder_adapter_component.md 準拠
 * 外部ライブラリ不要・純粋TypeScriptによるパレット量子化 & LZW圧縮実装 (完全ロイヤリティフリー)
 */

export interface GifFrameOptions {
  /** フレーム遅延時間 (1/100秒単位, 例: 30fps = 3) */
  delayCentiseconds: number;
}

export interface GifEncodeResult {
  blob: Blob;
  mimeType: 'image/gif';
}

export class GifEncoder {
  private isCancelled: boolean = false;

  /**
   * ImageDataフレーム列をアニメーションGIF (Blob) にエンコード
   */
  public async encode(
    frames: ImageData[],
    fps: number = 30,
    onProgress?: (progress: number) => void
  ): Promise<GifEncodeResult> {
    if (frames.length === 0) {
      throw new Error('No frames provided to GifEncoder.');
    }

    this.isCancelled = false;

    const width = frames[0].width;
    const height = frames[0].height;
    const delayCentiseconds = Math.max(1, Math.round(100 / Math.max(1, fps)));

    const bytes: number[] = [];

    // 1. GIF89a ヘッダー (6 bytes)
    this.writeString(bytes, 'GIF89a');

    // 2. Logical Screen Descriptor (7 bytes)
    this.writeShort(bytes, width);
    this.writeShort(bytes, height);
    // Packed fields: Global Color Tableなし (各フレームで高精度Local Color Tableを使用)
    bytes.push(0x70); // 0b01110000: No GCT, 8 bits/pixel color resolution
    bytes.push(0x00); // Background Color Index
    bytes.push(0x00); // Pixel Aspect Ratio (None)

    // 3. NETSCAPE 2.0 無限ループ拡張ブロック (19 bytes)
    bytes.push(0x21); // Extension Introducer
    bytes.push(0xff); // Application Extension Label
    bytes.push(0x0b); // Block Size: 11 bytes
    this.writeString(bytes, 'NETSCAPE2.0');
    bytes.push(0x03); // Sub-block length: 3 bytes
    bytes.push(0x01); // Loop sub-block ID
    this.writeShort(bytes, 0); // Loop count: 0 (無限ループ)
    bytes.push(0x00); // Block Terminator

    // 4. 各フレームのエンコード
    const totalFrames = frames.length;
    for (let i = 0; i < totalFrames; i++) {
      if (this.isCancelled) {
        throw new Error('Export cancelled');
      }

      const frame = frames[i];
      this.encodeFrame(bytes, frame, width, height, delayCentiseconds);

      if (onProgress) {
        onProgress((i + 1) / totalFrames);
      }

      // イベントループを解放してブラウザの応答性を維持
      if (i % 5 === 0) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }

    // 5. GIF Trailer (1 byte)
    bytes.push(0x3b);

    const uint8Array = new Uint8Array(bytes);
    const blob = new Blob([uint8Array], { type: 'image/gif' });

    return {
      blob,
      mimeType: 'image/gif',
    };
  }

  /**
   * 処理中断
   */
  public cancel(): void {
    this.isCancelled = true;
  }

  /**
   * 単一フレームのブロック書き込み
   */
  private encodeFrame(
    bytes: number[],
    frame: ImageData,
    width: number,
    height: number,
    delayCentiseconds: number
  ): void {
    // 1. パレット量子化 (最大256色)
    const { palette, indexedPixels } = this.quantizeFrame(frame.data, width, height);

    // 2. Graphic Control Extension (8 bytes)
    bytes.push(0x21); // Extension Introducer
    bytes.push(0xf9); // Graphic Control Label
    bytes.push(0x04); // Block size: 4 bytes
    bytes.push(0x04); // Packed: Disposal method = 1 (Do not dispose), no transparency
    this.writeShort(bytes, delayCentiseconds); // Delay time
    bytes.push(0x00); // Transparent color index
    bytes.push(0x00); // Block Terminator

    // 3. Image Descriptor (10 bytes)
    bytes.push(0x2c); // Image Separator
    this.writeShort(bytes, 0); // Left
    this.writeShort(bytes, 0); // Top
    this.writeShort(bytes, width);
    this.writeShort(bytes, height);
    // Packed: Local Color Table Flag (1), Not Interlaced (0), Not Sorted (0), Size of LCT (7 => 256 colors)
    bytes.push(0x87); // 0b10000111

    // 4. Local Color Table (256 * 3 = 768 bytes)
    for (let c = 0; c < 256; c++) {
      if (c < palette.length) {
        bytes.push(palette[c][0], palette[c][1], palette[c][2]);
      } else {
        bytes.push(0, 0, 0); // パディング
      }
    }

    // 5. Image Data (LZW compressed)
    this.writeLZW(bytes, indexedPixels);
  }

  /**
   * 高速パレット量子化 (5-bit RGBカラーバケットによる代表色抽出 & 最近傍インデックス付け)
   */
  private quantizeFrame(
    data: Uint8ClampedArray,
    width: number,
    height: number
  ): { palette: number[][]; indexedPixels: Uint8Array } {
    const totalPixels = width * height;
    const indexedPixels = new Uint8Array(totalPixels);

    // 1. カラーヒストグラム集計 (5-bit RGB = 32x32x32 = 32768 バケット)
    const histogram = new Map<number, { count: number; r: number; g: number; b: number }>();
    const step = totalPixels > 40000 ? 2 : 1; // 画素数が多い場合はサンプリングで高速化

    for (let i = 0; i < data.length; i += 4 * step) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const r5 = r >> 3;
      const g5 = g >> 3;
      const b5 = b >> 3;
      const key = (r5 << 10) | (g5 << 5) | b5;

      const entry = histogram.get(key);
      if (entry) {
        entry.count++;
        entry.r += r;
        entry.g += g;
        entry.b += b;
      } else {
        histogram.set(key, { count: 1, r, g, b });
      }
    }

    // 2. 頻出上位256色をパレットとして選出
    const sortedColors = Array.from(histogram.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 256);

    const palette: number[][] = sortedColors.map((c) => [
      Math.round(c.r / c.count),
      Math.round(c.g / c.count),
      Math.round(c.b / c.count),
    ]);

    // 色数が不足している場合の最低保証
    if (palette.length === 0) {
      palette.push([0, 0, 0]);
    }

    // 3. 各ピクセルをパレットの最近傍色へマッピング
    // キャッシュマップ (高速化用)
    const colorCache = new Map<number, number>();

    for (let i = 0, p = 0; p < totalPixels; i += 4, p++) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const key = (r << 16) | (g << 8) | b;

      let bestIndex = colorCache.get(key);
      if (bestIndex === undefined) {
        let minDist = Infinity;
        bestIndex = 0;
        for (let c = 0; c < palette.length; c++) {
          const pr = palette[c][0];
          const pg = palette[c][1];
          const pb = palette[c][2];
          const dist = (r - pr) * (r - pr) + (g - pg) * (g - pg) + (b - pb) * (b - pb);
          if (dist < minDist) {
            minDist = dist;
            bestIndex = c;
            if (dist === 0) break;
          }
        }
        colorCache.set(key, bestIndex);
      }

      indexedPixels[p] = bestIndex;
    }

    return { palette, indexedPixels };
  }

  /**
   * LZW 辞書圧縮 & サブブロック書き込み
   */
  private writeLZW(bytes: number[], indexedPixels: Uint8Array): void {
    const minCodeSize = 8; // 256 colors
    bytes.push(minCodeSize); // LZW Minimum Code Size

    const clearCode = 1 << minCodeSize; // 256
    const eoiCode = clearCode + 1; // 257

    let codeSize = minCodeSize + 1; // 9 bits
    let nextCode = eoiCode + 1; // 258

    const dictionary = new Map<number, number>();

    const resetDictionary = () => {
      dictionary.clear();
      codeSize = minCodeSize + 1;
      nextCode = eoiCode + 1;
    };

    // ビット出力バッファ管理
    const packet: number[] = [];
    let curAccum = 0;
    let curBits = 0;

    const flushPacket = () => {
      if (packet.length > 0) {
        bytes.push(packet.length);
        for (let i = 0; i < packet.length; i++) {
          bytes.push(packet[i]);
        }
        packet.length = 0;
      }
    };

    const outputCode = (code: number) => {
      curAccum |= code << curBits;
      curBits += codeSize;

      while (curBits >= 8) {
        packet.push(curAccum & 0xff);
        curAccum >>= 8;
        curBits -= 8;

        if (packet.length === 254) {
          flushPacket();
        }
      }

      // コードサイズ拡張
      if (nextCode >= (1 << codeSize) && codeSize < 12) {
        codeSize++;
      }
    };

    // 初期 Clear Code を出力
    outputCode(clearCode);
    resetDictionary();

    if (indexedPixels.length > 0) {
      let currentPrefix = indexedPixels[0];

      for (let i = 1; i < indexedPixels.length; i++) {
        const nextPixel = indexedPixels[i];
        const key = (currentPrefix << 8) | nextPixel;

        const code = dictionary.get(key);
        if (code !== undefined) {
          currentPrefix = code;
        } else {
          outputCode(currentPrefix);

          if (nextCode < 4096) {
            dictionary.set(key, nextCode++);
          } else {
            // 辞書オーバーフロー時はClearCodeを出力してリセット
            outputCode(clearCode);
            resetDictionary();
          }

          currentPrefix = nextPixel;
        }
      }

      outputCode(currentPrefix);
    }

    // 終端 EOI Code を出力
    outputCode(eoiCode);

    // 残りのビットをフラッシュ
    if (curBits > 0) {
      packet.push(curAccum & 0xff);
    }
    flushPacket();

    // 終端ブロック (0x00)
    bytes.push(0x00);
  }

  private writeString(bytes: number[], str: string): void {
    for (let i = 0; i < str.length; i++) {
      bytes.push(str.charCodeAt(i));
    }
  }

  private writeShort(bytes: number[], val: number): void {
    bytes.push(val & 0xff);
    bytes.push((val >> 8) & 0xff);
  }
}
